import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Check as CheckIcon } from '@mui/icons-material';
import { ScoringRubricItem } from '@/types';

interface RubricDetailsProps {
  userRubric: ScoringRubricItem[];
}

export const RubricDetails: React.FC<RubricDetailsProps> = ({ userRubric }) => {
  // Helper function to get status icon and color
  const getStatusInfo = (item: ScoringRubricItem) => {
    if (item.pointsScored >= item.totalPoints) {
      return { 
        icon: <CheckIcon sx={{ fontSize: 20, color: 'primary.main' }} />, 
        color: '#4ade80', 
        label: 'Complete' 
      };
    } else if (item.pointsScored > 0) {
      return { 
        icon: (
          <Box sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: '#fbbf24',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} />
        ), 
        color: '#fbbf24', 
        label: 'Partial' 
      };
    } else {
      return { 
        icon: (
          <Box sx={{ 
            width: 16, 
            height: 16, 
            borderRadius: '50%', 
            border: 1, 
            borderColor: 'text.primary',
            mx: 'auto'
          }} />
        ), 
        color: '#6b7280', 
        label: 'Not Started' 
      };
    }
  };

  // Helper function to parse instructions from semicolon-separated string
  const parseInstructions = (instructions: string | undefined) => {
    if (!instructions || instructions.trim() === '') return [];
    return instructions
      .split(';')
      .map(inst => inst.trim())
      .filter(inst => inst.length > 0);
  };

  // Helper function to format messages for display
  const formatMessage = (message: string) => {
    // Check if message already has Pharmacist/Patient prefix
    if (message.includes('Pharmacist:') || message.includes('Patient:') || 
        message.includes('Medical Provider:') || message.includes('Provider:')) {
      return message;
    }
    // If no prefix, return as-is (shouldn't happen with current implementation)
    return message;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {userRubric.map((item, index) => {
        const statusInfo = getStatusInfo(item);
        const instructions = parseInstructions(item.categorySpecificInstructions);
        const hasInstructions = instructions.length > 0;
        const hasEvidence = item.messages && item.messages.length > 0;
        const hasNotes = item.evaluationNotes && item.evaluationNotes.trim() !== '';

        return (
          <Accordion 
            key={index}
            sx={{
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid rgba(71, 85, 105, 0.3)',
              '&:before': {
                display: 'none',
              },
              '&.Mui-expanded': {
                margin: '8px 0',
              },
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}
              sx={{
                minHeight: 56,
                '&.Mui-expanded': {
                  minHeight: 56,
                },
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                pr: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20 }}>
                    {statusInfo.icon}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {item.scoringCategory}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${item.pointsScored}/${item.totalPoints}`}
                    size="small"
                    sx={{
                      bgcolor: statusInfo.color,
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                  <Chip
                    label={statusInfo.label}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: statusInfo.color,
                      color: statusInfo.color,
                    }}
                  />
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                {/* Instructions Section */}
                <Box>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: 'primary.main',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    📋 Instructions
                  </Typography>
                  {hasInstructions ? (
                    <Box sx={{ pl: 2 }}>
                      {instructions.map((instruction, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            mb: 0.5,
                            '&:before': {
                              content: '"• "',
                              color: 'primary.main',
                              fontWeight: 'bold',
                            },
                          }}
                        >
                          {instruction}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: 'text.disabled',
                      fontStyle: 'italic',
                      pl: 2
                    }}>
                      No specific instructions provided
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: 'rgba(71, 85, 105, 0.3)' }} />

                {/* Evidence Section */}
                <Box>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: 'primary.main',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    💬 Evidence {hasEvidence && `(${item.messages.length} messages)`}
                  </Typography>
                  {hasEvidence ? (
                    <Box sx={{ pl: 2 }}>
                      {item.messages.map((message, idx) => {
                        const isPharmacist = message.includes('Pharmacist:') || 
                                           message.includes('Medical Provider:') || 
                                           message.includes('Provider:');
                        const isPatient = message.includes('Patient:');
                        
                        return (
                          <Box
                            key={idx}
                            sx={{
                              mb: 1,
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: isPharmacist 
                                ? 'rgba(59, 130, 246, 0.1)' 
                                : isPatient 
                                  ? 'rgba(34, 197, 94, 0.1)'
                                  : 'rgba(107, 114, 128, 0.1)',
                              border: '1px solid',
                              borderColor: isPharmacist 
                                ? 'rgba(59, 130, 246, 0.3)' 
                                : isPatient 
                                  ? 'rgba(34, 197, 94, 0.3)'
                                  : 'rgba(107, 114, 128, 0.3)',
                            }}
                          >
                            <Typography variant="body2" sx={{ 
                              color: 'text.primary',
                              '& strong': {
                                color: isPharmacist ? '#3b82f6' : isPatient ? '#22c55e' : '#6b7280'
                              }
                            }}>
                              {formatMessage(message).split(':').map((part, partIdx) => 
                                partIdx === 0 && (part.includes('Pharmacist') || part.includes('Provider') || part.includes('Patient'))
                                  ? <strong key={partIdx}>{part}:</strong>
                                  : partIdx === 0 ? part : ` ${part}`
                              )}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: 'text.disabled',
                      fontStyle: 'italic',
                      pl: 2
                    }}>
                      No evidence collected yet
                    </Typography>
                  )}
                </Box>

                {/* Evaluation Notes Section */}
                {hasNotes && (
                  <>
                    <Divider sx={{ borderColor: 'rgba(71, 85, 105, 0.3)' }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600, 
                        color: 'primary.main',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        📝 Evaluation Notes
                      </Typography>
                      <Box sx={{
                        pl: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(107, 114, 128, 0.1)',
                        border: '1px solid rgba(107, 114, 128, 0.3)',
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.5
                        }}>
                          {item.evaluationNotes}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
      
      {userRubric.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          color: 'text.disabled'
        }}>
          <Typography variant="h6" gutterBottom>
            No scoring rubric available
          </Typography>
          <Typography variant="body2">
            This case doesn't have detailed scoring criteria.
          </Typography>
        </Box>
      )}
    </Box>
  );
};